import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'
import { getUserProfile, markWelcomeSeen } from '@/lib/coreApi'

/**
 * One-time welcome modal shown to newly-confirmed users.
 * Explains the pool, rally board, weight system, and what to do next.
 */
export default function WelcomeModal() {
  const { session, profile } = useAuth()
  const [show, setShow] = useState(false)
  const [checked, setChecked] = useState(false)

  const role = (profile?.role || '').toLowerCase()
  const isNomineeRole = ['teacher', 'coach', 'prospect', 'athlete'].includes(role)

  useEffect(() => {
    if (!session?.user?.id) { setChecked(true); return }
    if (!isNomineeRole) { setChecked(true); return }
    if (checked) return

    getUserProfile()
      .then((res) => {
        if (!res?.data?.welcomed_at) setShow(true)
      })
      .catch(() => { /* stub profile will have null welcomed_at */ })
      .finally(() => setChecked(true))
  }, [session?.user?.id, isNomineeRole, checked])

  const handleDismiss = useCallback(async () => {
    setShow(false)
    try { await markWelcomeSeen() } catch { /* non-blocking */ }
  }, [])

  if (!show) return null

  const firstName = (profile?.display_name || profile?.full_name || '').split(' ')[0] || 'friend'

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" onClick={handleDismiss} />
      <div className="fixed left-1/2 top-1/2 z-50 w-[min(560px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-white shadow-2xl">
        <div className="rounded-t-3xl bg-gradient-to-br from-cocoa to-cocoa/80 p-6 text-center text-white">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-2xl">
            🎉
          </div>
          <h2 className="text-2xl font-bold">Welcome to the Pool, {firstName}!</h2>
          <p className="mt-1 text-sm text-white/80">
            Your nomination is confirmed. Here's what happens next.
          </p>
        </div>

        <div className="space-y-5 p-6">
          <section>
            <h3 className="flex items-center gap-2 text-sm font-bold text-cocoa">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cocoa text-xs text-white">1</span>
              You're in the draw pool
            </h3>
            <p className="mt-1 pl-7 text-sm text-cocoa/70">
              Every nominee in the pool has a <strong>weight</strong>. Higher weights get better odds in the weighted random draw.
              You start at weight 1. When a ceremony happens, the system picks winners based on weights.
            </p>
          </section>

          <section>
            <h3 className="flex items-center gap-2 text-sm font-bold text-cocoa">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cocoa text-xs text-white">2</span>
              Rally support to raise your weight
            </h3>
            <p className="mt-1 pl-7 text-sm text-cocoa/70">
              You'll get a <strong>support link</strong> unique to you. Share it with your community.
              Every person who clicks through and supports you adds +1 to your weight.
            </p>
          </section>

          <section>
            <h3 className="flex items-center gap-2 text-sm font-bold text-cocoa">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cocoa text-xs text-white">3</span>
              Post to the Rally Board
            </h3>
            <p className="mt-1 pl-7 text-sm text-cocoa/70">
              The <Link to="/rally" className="underline font-semibold">Rally Board</Link> is a public cork board where you can pin a message for 72 hours.
              It's your chance to tell the community why they should support you.
            </p>
          </section>

          <section>
            <h3 className="flex items-center gap-2 text-sm font-bold text-cocoa">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cocoa text-xs text-white">4</span>
              If you win the draw
            </h3>
            <p className="mt-1 pl-7 text-sm text-cocoa/70">
              {role === 'teacher' ? (
                <>Your classroom gets created and funded. You'll get access to your teacher portal to manage your classroom, budget, and public page.</>
              ) : role === 'coach' ? (
                <>Your locker room gets created and funded. Your nominated athletes are added to your roster. You'll manage budgets, the roster, and your public page.</>
              ) : (
                <>You'll be added to your coach's locker room roster with a budget allocation and access to the full athlete experience.</>
              )}
            </p>
          </section>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-sand/50 p-5">
          <Link
            to="/pool"
            onClick={handleDismiss}
            className="text-sm font-semibold text-cocoa/70 hover:text-cocoa"
          >
            View the Pool →
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/rally"
              onClick={handleDismiss}
              className="rounded-xl border border-cocoa/20 px-4 py-2 text-sm font-semibold text-cocoa hover:bg-sand/30"
            >
              Rally Board
            </Link>
            <button
              type="button"
              onClick={handleDismiss}
              className="rounded-xl bg-cocoa px-5 py-2 text-sm font-semibold text-white hover:bg-cocoa/90"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
